/**
 * /attendance/manual-clock — HR records a punch for an employee (ADR-0014
 * Phase 4 PR-3). POST /v1/attendance/clock. HR-gated.
 *
 * Uses an inline employee <select> (from listEmployees) rather than
 * EmployeePickerModal: that modal navigates on pick (it's loan-flow-specific),
 * whereas a form needs the selection returned in place. Same listEmployees
 * source the picker uses.
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { authedLayoutRoute } from './authed.js';
import { useAuth } from '../auth/AuthContext.js';
import { manualClock } from '../api/attendance.js';
import { listEmployees } from '../api/employees.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

export const attendanceManualClockRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/attendance/manual-clock',
  component: AttendanceManualClockPage,
});

const FormSchema = z.object({
  employeeId: z.string().min(1, 'Select an employee'),
  direction: z.enum(['clock_in', 'clock_out']),
  // datetime-local value (no timezone); converted to ISO on submit.
  occurredAt: z.string().min(1, 'Required'),
  note: z.string().max(500).optional(),
});
type FormValues = z.infer<typeof FormSchema>;

function AttendanceManualClockPage(): JSX.Element {
  const { user } = useAuth();
  const isHr = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'hr';
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const employeesQuery = useQuery({ queryKey: ['employees', 'picker'], queryFn: () => listEmployees({ status: 'active', limit: 200 }) });

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { employeeId: '', direction: 'clock_in', occurredAt: '', note: '' },
  });

  const mut = useMutation({
    mutationFn: (v: FormValues) =>
      manualClock({
        employeeId: v.employeeId,
        direction: v.direction,
        occurredAt: new Date(v.occurredAt).toISOString(),
        ...(v.note ? { note: v.note } : {}),
      }),
    onSuccess: () => {
      setError(null);
      setSuccess('Punch recorded.');
      reset({ employeeId: '', direction: 'clock_in', occurredAt: '', note: '' });
    },
    onError: (err: unknown) => {
      setSuccess(null);
      setError(err instanceof ApiError ? err.message : 'Could not record the punch.');
    },
  });

  if (!isHr) {
    return (
      <section className="p-8 max-w-3xl">
        <Alert variant="error">Restricted to HR / admin / owner roles.</Alert>
      </section>
    );
  }

  const employees = employeesQuery.data?.employees ?? [];
  const inputCls = 'mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm';

  return (
    <section className="p-8 space-y-6 max-w-xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Manual clock</h1>
        <p className="text-sm text-gray-400">Record a punch for an employee — e.g. when they forgot to clock or a device missed it.</p>
      </header>

      <form onSubmit={handleSubmit((v) => mut.mutate(v))} className="rounded border border-gray-900 p-4 space-y-3" noValidate>
        {success ? <div role="status" className="rounded border border-green-900 bg-green-950/40 px-3 py-2 text-sm text-green-300">{success}</div> : null}

        <label className="block text-xs uppercase text-gray-400">
          Employee
          <select {...register('employeeId')} className={inputCls}>
            <option value="">Select an employee…</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.employeeNumber})</option>
            ))}
          </select>
          {errors.employeeId ? <span className="text-xs text-red-400">{errors.employeeId.message}</span> : null}
        </label>

        <label className="block text-xs uppercase text-gray-400">
          Direction
          <select {...register('direction')} className={inputCls}>
            <option value="clock_in">Clock in</option>
            <option value="clock_out">Clock out</option>
          </select>
        </label>

        <label className="block text-xs uppercase text-gray-400">
          When
          <input type="datetime-local" {...register('occurredAt')} className={inputCls} />
          {errors.occurredAt ? <span className="text-xs text-red-400">{errors.occurredAt.message}</span> : null}
        </label>

        <label className="block text-xs uppercase text-gray-400">
          Note (optional)
          <textarea {...register('note')} rows={2} className={inputCls} placeholder="e.g. forgot badge" />
        </label>

        {error ? <Alert variant="error">{error}</Alert> : null}
        <button type="submit" disabled={isSubmitting || mut.isPending} className="rounded bg-white text-black text-sm font-medium px-4 py-2 disabled:opacity-50">
          {mut.isPending ? 'Recording…' : 'Record punch'}
        </button>
      </form>
    </section>
  );
}
